import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, WifiHigh, WifiSlash, Sun, Moon } from "@phosphor-icons/react";
import { api } from "../lib/api";

const RING_RADIUS = 54;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function useLocalTheme(token) {
    const storageKey = `vista-sede-theme-${token}`;
    const [theme, setThemeState] = useState(() => {
        try {
            return localStorage.getItem(storageKey) || "dark";
        } catch {
            return "dark";
        }
    });
    const setTheme = (t) => {
        setThemeState(t);
        try {
            localStorage.setItem(storageKey, t);
        } catch {
            // storage non disponibile (es. modalità privata): il tema resta solo per questa sessione
        }
    };
    return [theme, setTheme];
}

export default function VistaSede() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [remaining, setRemaining] = useState(null);
    const [error, setError] = useState(false);
    const [online, setOnline] = useState(true);
    const [theme, setTheme] = useLocalTheme(token);
    const isLight = theme === "light";
    const codeRef = useRef(null);

    const fetchCode = async () => {
        try {
            const { data } = await api.get(`/site-code/display/${token}`);
            codeRef.current = data.code;
            setInfo(data);
            setRemaining(data.seconds_remaining);
            setError(false);
            setOnline(true);
        } catch (e) {
            if (e.response?.status === 404) setError(true);
            else setOnline(false);
        }
    };

    useEffect(() => {
        fetchCode();
        const poll = setInterval(fetchCode, 5000);
        return () => clearInterval(poll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        const tick = setInterval(() => {
            setRemaining((r) => {
                if (r === null) return r;
                if (r <= 1) {
                    fetchCode();
                    return 0;
                }
                return r - 1;
            });
        }, 1000);
        return () => clearInterval(tick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (error) {
        return (
            <div
                className={`min-h-screen flex items-center justify-center p-8 ${isLight ? "bg-white text-ink" : "bg-ink text-white"}`}
                data-testid="vista-sede-not-found"
            >
                <div className="text-center">
                    <div className="overline text-zinc-400 mb-3">404</div>
                    <h1 className="text-3xl font-black tracking-tighter">Pagina non trovata</h1>
                    <p className="text-sm text-zinc-400 mt-2">Il link non è valido. Chiedi al titolare il link corretto di &quot;Vista sede&quot;.</p>
                </div>
            </div>
        );
    }

    const windowSeconds = info?.window_seconds || 600;
    const pct = remaining !== null ? Math.max(0, remaining / windowSeconds) : 1;
    const dashoffset = RING_CIRCUMFERENCE * (1 - pct);
    const mins = remaining !== null ? Math.floor(remaining / 60) : "…";
    const secs = remaining !== null ? String(remaining % 60).padStart(2, "0") : "";

    return (
        <div
            className={`min-h-screen flex flex-col items-center justify-center p-6 select-none relative ${
                isLight ? "bg-white text-ink" : "bg-ink text-white"
            }`}
            data-testid="vista-sede-page"
        >
            <button
                type="button"
                onClick={() => setTheme(isLight ? "dark" : "light")}
                className={`absolute top-4 right-4 w-9 h-9 flex items-center justify-center border transition-colors ${
                    isLight ? "border-zinc-200 text-zinc-500 hover:text-ink" : "border-zinc-700 text-zinc-500 hover:text-white"
                }`}
                data-testid="vista-sede-theme-toggle"
                aria-label={isLight ? "Passa al tema scuro" : "Passa al tema chiaro"}
                title={isLight ? "Tema scuro" : "Tema chiaro"}
            >
                {isLight ? <Moon size={16} weight="bold" /> : <Sun size={16} weight="bold" />}
            </button>

            <div className="flex items-center gap-2 mb-8 text-zinc-500">
                <QrCode size={18} weight="bold" />
                <span className="overline">{info?.agency_name || "Vista sede"}</span>
                {online ? (
                    <WifiHigh size={16} className="text-emerald-500" data-testid="vista-sede-online" />
                ) : (
                    <WifiSlash size={16} className="text-amber-500" data-testid="vista-sede-offline" />
                )}
            </div>

            <div
                className={`bg-white p-5 md:p-6 shadow-2xl ${isLight ? "border border-zinc-200" : ""}`}
                data-testid="vista-sede-qr"
            >
                {info?.code ? (
                    <QRCodeSVG value={info.code} size={260} level="M" />
                ) : (
                    <div className="w-[260px] h-[260px] flex items-center justify-center text-zinc-300 text-xs">
                        Caricamento…
                    </div>
                )}
            </div>

            <div
                className="mt-10 text-6xl md:text-8xl font-black tracking-[0.2em] tabular-nums"
                data-testid="vista-sede-code"
            >
                {info?.code || "······"}
            </div>

            <div className="mt-10 flex items-center gap-4" data-testid="vista-sede-countdown">
                <svg width="120" height="120" viewBox="0 0 120 120" className="-rotate-90">
                    <circle cx="60" cy="60" r={RING_RADIUS} fill="none" stroke={isLight ? "#e4e4e7" : "#27272a"} strokeWidth="8" />
                    <circle
                        cx="60"
                        cy="60"
                        r={RING_RADIUS}
                        fill="none"
                        stroke="#ff2a00"
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={dashoffset}
                        style={{ transition: "stroke-dashoffset 1s linear" }}
                    />
                </svg>
                <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500">Rinnovo tra</div>
                    <div className="text-2xl font-black tabular-nums">
                        {mins}:{secs || "00"}
                    </div>
                </div>
            </div>

            <p className="mt-12 max-w-md text-center text-xs text-zinc-500 leading-relaxed">
                Inquadra il QR o digita il codice nell&apos;app per aprire il turno da smartphone.
                Il codice resta valido per qualche istante anche dopo il rinnovo, per chi stava già
                scansionando o digitando.
            </p>
        </div>
    );
}
